import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import ProductCard from '../components/ProductCard';
import { useFormPopupContext } from '../contexts/FormPopupContext';
import { products, awards, testimonials } from '../utils/data';

const Home: React.FC = () => {
  const { openFormPopup } = useFormPopupContext();

  return (
    <div>
      {/* Hero Section */}
      <Hero
        title="Where Engineering Meets Artistry"
        subtitle="Discover the ultimate fusion of optical precision and acoustic excellence. Parallax Cinema transforms spaces into cinematic sanctuaries."
        backgroundImage="/images/hero-section-1.png"
        mobileBackgroundImage="/images/mobile-hero-1.png"
        primaryCTA={{
          text: "Schedule Private Consultation",
          onClick: openFormPopup
        }}
        secondaryCTA={{
          text: "Explore Products",
          href: "/products"
        }}
        height="full"
      />

      {/* Product Showcase Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-gradient">Precision-Engineered Excellence</span>
            </h2>
            <p className="text-xl text-secondary max-w-3xl mx-auto leading-relaxed">
              Each product represents the pinnacle of audio-visual engineering, 
              meticulously crafted for the most discerning enthusiasts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Featured Product */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {/* Featured Product Card */}
              <ProductCard product={products[0]} featured={true} />
              
              {/* Hero Image Section */}
              <motion.div
                className="mt-8 relative overflow-hidden rounded-xl glass-effect"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="relative h-[400px] lg:h-[500px]">
                  <img
                    src="/images/parallax-card-image.png"
                    alt="Parallax Cinema - Where Engineering Meets Artistry"
                    className="w-full h-full object-cover object-center"
                  />
                  {/* Cinematic Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  
                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      viewport={{ once: true }}
                    >
                      <h3 className="text-2xl lg:text-3xl font-bold text-accent mb-3">
                        Immersive Cinema Experience
                      </h3>
                      <p className="text-lg text-secondary leading-relaxed max-w-2xl">
                        Transform your space into a world-class cinema with precision-engineered 
                        projection technology that brings every scene to life.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Other Products */}
            <div className="space-y-8">
              {products.slice(1).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-8">
                <span className="text-gradient">Innovation & Engineering</span>
              </h2>
              <p className="text-lg text-secondary mb-8 leading-relaxed">
                Our commitment to technical excellence drives every aspect of product development. 
                From proprietary optical systems to advanced acoustic engineering, we push the 
                boundaries of what's possible in home cinema technology.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="glass-effect p-6 rounded-lg">
                  <h3 className="text-highlight font-semibold mb-2">Optical Precision</h3>
                  <p className="text-secondary text-sm">Advanced lens systems with motorized precision adjustments</p>
                </div>
                <div className="glass-effect p-6 rounded-lg">
                  <h3 className="text-highlight font-semibold mb-2">Acoustic Excellence</h3>
                  <p className="text-secondary text-sm">Proprietary driver technology and room calibration systems</p>
                </div>
                <div className="glass-effect p-6 rounded-lg">
                  <h3 className="text-highlight font-semibold mb-2">Premium Materials</h3>
                  <p className="text-secondary text-sm">Handcrafted cabinets using finest hardwood veneers</p>
                </div>
                <div className="glass-effect p-6 rounded-lg">
                  <h3 className="text-highlight font-semibold mb-2">Smart Integration</h3>
                  <p className="text-secondary text-sm">Intelligent room adaptation and seamless connectivity</p>
                </div>
              </div>

              <Link to="/experience" className="btn-primary">
                Explore Our Technology
              </Link>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="glass-effect p-8 rounded-xl">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-accent mb-4">Technical Specifications</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-secondary/20 pb-2">
                      <span className="text-secondary">Resolution</span>
                      <span className="text-accent font-mono">Native 8K (7680×4320)</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary/20 pb-2">
                      <span className="text-secondary">Brightness</span>
                      <span className="text-accent font-mono">4,000+ lumens</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary/20 pb-2">
                      <span className="text-secondary">Color Gamut</span>
                      <span className="text-accent font-mono">DCI-P3 100%</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary/20 pb-2">
                      <span className="text-secondary">Frequency Response</span>
                      <span className="text-accent font-mono">20Hz - 40kHz</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-highlight mb-2">99.9%</div>
                  <div className="text-secondary text-sm">Color Accuracy Rating</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-gradient">Experience the Extraordinary</span>
            </h2>
            <p className="text-xl text-secondary max-w-3xl mx-auto leading-relaxed">
              By appointment only. Discover your perfect setup through our exclusive 
              consultation and showroom experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <motion.div
              className="glass-effect p-8 rounded-xl text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-accent mb-4">Private Showroom</h3>
              <p className="text-secondary leading-relaxed">
                Experience our complete product lineup in an acoustically optimized environment 
                designed to showcase the full potential of each system.
              </p>
            </motion.div>

            <motion.div
              className="glass-effect p-8 rounded-xl text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-accent mb-4">Expert Consultation</h3>
              <p className="text-secondary leading-relaxed">
                Work directly with our audio-visual specialists to design the perfect 
                system tailored to your space, preferences, and lifestyle.
              </p>
            </motion.div>

            <motion.div
              className="glass-effect p-8 rounded-xl text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-accent mb-4">White-Glove Installation</h3>
              <p className="text-secondary leading-relaxed">
                Our certified technicians provide complete installation and calibration 
                services to ensure optimal performance in your environment.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-4 max-w-md mx-auto">
              <button 
                onClick={openFormPopup}
                className="btn-primary flex-1 sm:flex-none sm:min-w-[200px] whitespace-nowrap"
              >
                Schedule Consultation
              </button>
              <Link 
                to="/contact" 
                className="btn-secondary flex-1 sm:flex-none sm:min-w-[140px] whitespace-nowrap"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-gradient">Award-Winning Excellence</span>
            </h2>
            <p className="text-xl text-secondary max-w-3xl mx-auto leading-relaxed">
              Recognition from industry leaders validates our commitment to innovation and quality.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {awards.map((award, index) => (
              <motion.div
                key={index}
                className="glass-effect p-6 rounded-lg text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-highlight" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-accent mb-2">{award.title}</h3>
                <p className="text-sm text-secondary mb-1">{award.organization}</p>
                <p className="text-xs text-highlight">{award.year}</p>
              </motion.div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="glass-effect p-6 rounded-lg"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="mb-4">
                  <div className="flex text-highlight mb-2">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-secondary leading-relaxed mb-4">"{testimonial.content}"</p>
                </div>
                <div>
                  <p className="font-semibold text-accent">{testimonial.name}</p>
                  <p className="text-sm text-secondary">{testimonial.role}</p>
                  {testimonial.company && (
                    <p className="text-xs text-highlight">{testimonial.company}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popup handled globally via FormPopupProvider */}
    </div>
  );
};

export default Home; 